import { z } from "zod";
import Label from "../../../ui/Label/Label";
import ConfirmationModal from "../../../ui/Modal/ConfirmationModal";
import Modal from "../../../ui/Modal/Modal";
import TextInput from "../../../ui/TextInput/TextInput";
import styles from "./SetupWordpressModal.module.scss";
import { Controller, useForm } from "react-hook-form";
import urls from "../../../routes";
import { useToast } from "../../../ui/Toast/ToastContext";
import { zodResolver } from "@hookform/resolvers/zod";

const getWordpressCallbackUrl = (url: URL) => {
  const searchParams = new URLSearchParams();
  searchParams.set(
    "success_url",
    `${window.location.origin}${urls.integrationsWordpressCallback}`,
  );
  searchParams.set(
    "app_name",
    `Macaw - ${Math.floor(new Date().getTime() / 1000)}`,
  );

  const path = url.pathname.endsWith("/")
    ? url.pathname.slice(0, url.pathname.length - 1)
    : url.pathname;

  if (path) {
    return `${
      url.origin
    }${path}/wp-admin/authorize-application.php?${searchParams.toString()}`;
  }

  return `${
    url.origin
  }/wp-admin/authorize-application.php?${searchParams.toString()}`;
};

const schema = z.object({ url: z.string() });

function SetupWordpressModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { control, getValues, trigger } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });
  const { showToast } = useToast();
  const onSubmit = async () => {
    const valid = await trigger();

    if (valid) {
      const url = getValues("url");

      try {
        const urlObject = new URL(
          !url.startsWith("http://") && !url.startsWith("https://")
            ? `https://${url}`
            : url,
        );

        try {
          await fetch(urlObject.href, {
            method: "HEAD",
            mode: "no-cors",
          });

          window.location.href = getWordpressCallbackUrl(urlObject);
        } catch {
          showToast({
            title: "Website can't be reached",
            description: "Please check the URL and try again",
            variant: "error",
          });
        }
      } catch {
        showToast({
          title: "Invalid URL",
          description: "Please check the URL and try again",
          variant: "error",
        });
        return;
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Setup WordPress connection">
      <ConfirmationModal onSubmit={onSubmit} onClose={onClose}>
        <div className={styles.container}>
          <Controller
            control={control}
            name="url"
            render={({ field }) => (
              <Label label="Enter your WordPress site URL">
                <TextInput placeholder="https://www.website.com" {...field} />
              </Label>
            )}
          />
        </div>
      </ConfirmationModal>
    </Modal>
  );
}

export default SetupWordpressModal;
